import React, { useEffect, useState } from 'react';
import { Alert, LinearProgress } from '@mui/material';
import { Modal } from '../../../../../shared/ui/Modal';
import {
  Footer,
  FormBody,
  FormWrapper,
  Header,
  Section,
  SectionTitle,
} from '../../../../../shared/ui/Modal/Body';
import { TextInput } from '../../../../../shared/ui/TextInput';
import Button from '@mui/material/Button/Button';
import { DataTable } from '../../../../../shared/ui/dataTable';
import { PaginationState } from '@tanstack/react-table';
import { ProductDataSource } from '../../../../../datasources/catalog/ProductsDataSource';
import { Product } from '../../../../../types/product/Product';
import { ProductBasket } from '../../../../../types/basket/ProductBasket';
import {
  getGenericErrorMessage,
  throwIfGenericApiError,
} from '../../../../../datasources/GenericErrors';

type SelectProductProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (products: ProductBasket[]) => void;
  existingProducts: ProductBasket[];
};

export const SelectProduct: React.FC<SelectProductProps> = ({
  open,
  onClose,
  onConfirm,
  existingProducts,
}) => {
  const [products, setProducts] = useState<Product[]>();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchRetry, setSearchRetry] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<
    Map<number, ProductBasket>
  >(new Map());

  const datasource = new ProductDataSource();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const existingProductIds = new Set(
    existingProducts.map((product) => product.id)
  );
  const existingSapCodes = new Set(
    existingProducts.map((product) => product.sap_code.trim().toLowerCase())
  );
  const availableProducts = (products ?? []).filter(
    (product) =>
      !existingProductIds.has(product.id) &&
      !existingSapCodes.has(product.sap_code.trim().toLowerCase())
  );

  function handleSelectionChange(selectedRows: Product[]): void {
    const currentPageIds = new Set(
      (products ?? []).map((product) => product.id)
    );

    setSelectedProducts((previous) => {
      const next = new Map<number, ProductBasket>(
        Array.from(previous.entries()).filter(([id]) => !currentPageIds.has(id))
      );

      selectedRows.forEach((product) => {
        next.set(product.id, {
          id: product.id,
          sap_code: product.sap_code,
          description: product.description,
        });
      });

      return next;
    });
  }

  function handleConfirm(): void {
    onConfirm(Array.from(selectedProducts.values()));
    setSelectedProducts(new Map());
    onClose();
  }

  function handleCancel(): void {
    setSelectedProducts(new Map());
    onClose();
  }

  function handleSearchChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const value = event.target.value;
    setSearchText(value);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearchText(searchText.trim()),
      300
    );

    return () => window.clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    if (!open) {
      setSelectedProducts(new Map());
      return;
    }

    const normalizedSearch = debouncedSearchText;
    if (normalizedSearch.length > 0 && normalizedSearch.length < 3) {
      setProducts([]);
      setTotalRows(0);
      setSearchError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setSearchError(null);
    setLoading(true);

    async function loadData() {
      try {
        const isSapCode = /^\d+$/.test(normalizedSearch);
        const result = await datasource.QuerySalesProducts({
          ...(normalizedSearch
            ? isSapCode
              ? { sapCodePrefix: normalizedSearch }
              : { searchText: normalizedSearch }
            : {}),
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
        });
        throwIfGenericApiError(result);
        if (!Array.isArray(result.data) || !Number.isFinite(result.rows)) {
          throw new Error('A resposta da consulta de produtos está inválida.');
        }

        if (cancelled) return;

        setProducts(result.data);
        setTotalRows(result.rows);
      } catch (error) {
        if (!cancelled) {
          setProducts([]);
          setTotalRows(0);
          setSearchError(
            getGenericErrorMessage(
              error,
              'Não foi possível pesquisar os produtos.'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearchText,
    searchRetry,
  ]);

  return (
    <Modal open={open} width="min(720px, 95vw)">
      <Header>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#444',
          }}
        >
          Adicionar Produtos
        </span>
      </Header>
      <FormBody>
        <FormWrapper>
          <Section>
            <TextInput
              value={searchText}
              label="Buscar por nome ou código SAP"
              handleChange={handleSearchChange}
              helperText={
                searchText.trim().length > 0 && searchText.trim().length < 3
                  ? 'Digite pelo menos 3 caracteres para pesquisar.'
                  : undefined
              }
            />
          </Section>
          <Section>
            <SectionTitle>Produtos</SectionTitle>
            {loading && <LinearProgress />}
            {searchError && searchText.trim() === debouncedSearchText && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setSearchRetry((attempt) => attempt + 1)}
                    disabled={loading}
                  >
                    Tentar novamente
                  </Button>
                }
              >
                {searchError}
              </Alert>
            )}
            <DataTable
              key={open ? 'open' : 'closed'}
              data={availableProducts}
              columns={[
                {
                  id: 'description',
                  header: 'Produto',
                  accessorKey: 'description' as keyof Product,
                },
                {
                  id: 'sap_code',
                  header: 'Código SAP',
                  accessorKey: 'sap_code' as keyof Product,
                },
              ]}
              getRowId={(product) => String(product.id)}
              totalRows={totalRows}
              pagination={pagination}
              onPaginationChange={setPagination}
              onSelectionChange={handleSelectionChange}
              enableRowSelection
              enablePageSizeSelector={false}
              enablePagination
            />
          </Section>
        </FormWrapper>
      </FormBody>
      <Footer>
        <Button variant="outlined" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedProducts.size === 0}
        >
          Confirmar ({selectedProducts.size})
        </Button>
      </Footer>
    </Modal>
  );
};
